import { useNavigate } from "@entry/navigation";
import dayjs from "dayjs";
import { useState } from "react";

import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import {
  ChartFooter,
  ChartHeader,
  DepositAdjuster,
  ProjectedChart,
  ProjectionCalculationModal,
} from "@components/ProjectedChart";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Caption } from "@components/Typography";
import { ProjectedChartProvider } from "@contexts";
import { BeneficiaryType, DateFormat, Flow, Routes } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import {
  useAddBeneficiary,
  useAddGoal,
  useErrorBanner,
  useGoalFlow,
  useOnboarding,
  useProjectedChart,
} from "@hooks";
import { Translations, i18n } from "@i18n";
import {
  convertDollarToCents,
  getHarvestAddGoalVariables,
  getMinDepositAmount,
  getRouteForPath,
  getSipStartInfoKey,
} from "@utils";
import { styles } from "./styles";

export const ChildDepositRoute = () => {
  const {
    goalProps: { accountType },
  } = useGoalFlow();

  return (
    <ProjectedChartProvider accountType={accountType}>
      <ChildDepositRouteComponent />
    </ProjectedChartProvider>
  );
};

export const ChildDepositRouteComponent = () => {
  const navigate = useNavigate();
  const { resetToNext } = useOnboarding();

  const { showErrorBanner } = useErrorBanner();

  const [loading, setLoading] = useState(false);

  const {
    recurringAmount,
    recurringDepositType,
    projected,
    contributeAfter18,
    interest,
    projectedAge,
    currentAge,
  } = useProjectedChart();

  const {
    goalProps: {
      beneficiaryId: existingBeneficiaryId,
      beneficiaryType = BeneficiaryType.Child,
      firstName,
      lastName,
      dateOfBirth: dob,
      isFutureBeneficiary,
      accountType,
      flow,
      goalName,
      goalType,
      ssn,
      harvestStandardDeduction,
      harvestLongTermCapitalGains,
      harvestRecommendedFloorUsd,
    },
    setGoalProps,
  } = useGoalFlow();

  const goalLengthInYears = projectedAge - currentAge;
  const targetYear = dayjs().year() + goalLengthInYears;

  const targetAmount = projected[projected.length - 1].amount;

  const [addBeneficiary] = useAddBeneficiary();
  const [addGoal] = useAddGoal();

  const handleSubmit = async () => {
    const recurringDepositInCents = convertDollarToCents(recurringAmount);
    const dateOfBirth = isFutureBeneficiary ? null : dayjs(dob).format(DateFormat.User);

    try {
      if (!firstName || !lastName || !accountType) {
        return showErrorBanner();
      }

      if (!existingBeneficiaryId && !isFutureBeneficiary && !dayjs(dob).isValid()) {
        return showErrorBanner();
      }

      setLoading(true);

      // Reuse the beneficiary when one already exists, so re entering this
      // screen after a create cannot add a duplicate child.
      const beneficiaryId =
        existingBeneficiaryId ??
        (
          await addBeneficiary({
            variables: {
              firstName,
              lastName,
              dateOfBirth,
              type: beneficiaryType,
              taxId: ssn,
            },
          })
        ).data?.addBeneficiary?.id;

      if (beneficiaryId) {
        setGoalProps({ beneficiaryId });

        await addGoal({
          variables: {
            beneficiaryId,
            accountType,
            goalName,
            goalType,
            recurringDepositInCents,
            recurringDepositSchedule: recurringDepositType,
            targetAmountInCents: convertDollarToCents(Math.round(targetAmount)),
            targetYear,
            contributionsAfter18: contributeAfter18,
            projectedRateOfReturn: interest,
            ...getHarvestAddGoalVariables({
              accountType,
              harvestStandardDeduction,
              harvestLongTermCapitalGains,
              harvestRecommendedFloorUsd,
            }),
          },
        });

        if (flow === Flow.Accounts) {
          navigate(Routes.Accounts, { replace: true });
        } else {
          await resetToNext();
        }
      }
    } catch {
      showErrorBanner();
    } finally {
      setLoading(false);
    }
  };

  const sipStartInfoKey = getSipStartInfoKey(recurringDepositType);

  const minDepositAmount = getMinDepositAmount(accountType);

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.ChildDepositRouteTitle),
    // Same "Set your deposit" screen as GoalDepositRoute — web forked it by
    // beneficiary type where mobile keeps one GoalDepositScreen. Report both
    // under one name so the deposit step is a single step in the funnel.
    analyticsName: getRouteForPath(Routes.GoalDeposit) ?? undefined,
    hideLogout: flow !== Flow.Onboarding,
    cta: {
      primary: {
        title: i18n.t(Translations.ChildDepositRoutePrimaryCTA),
        onClick: handleSubmit,
        loading,
        disabled: recurringAmount < minDepositAmount,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      <ProjectedChart
        header={
          <ChartHeader
            title={i18n.t(Translations.ChildDepositRouteHeaderTitle, {
              age: projectedAge,
            })}
          />
        }
        footer={
          <ChartFooter
            projectedLabel={i18n.t(Translations.ChildDepositRouteFooterProjectedLabel, {
              age: projectedAge,
            })}
          />
        }
      />
      <Spacing size={SpacingSizes.M} />

      <DepositAdjuster />
      <Spacing size={SpacingSizes.M} />

      <ProjectionCalculationModal />

      {sipStartInfoKey ? (
        <>
          <Caption style={styles.sipStartInfo}>{i18n.t(sipStartInfoKey)}</Caption>
          <Spacing size={SpacingSizes.S} />
        </>
      ) : null}
    </OnboardingLayout>
  );
};
